import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	InputLabel,
	ListItemText,
	MenuItem,
	Select
} from '@mui/material';
import {
	areAuditTrailFiltersEqual,
	buildClearedCategoryFilters,
	cloneAppliedFilters,
	getCategoryGroupLabel,
	getExpandGroupLabel,
	getSourceFilterLabel
} from './auditTrailFilterUtils';
import {
	AUDIT_TRAIL_FILTER_MENU_PROPS,
	CATEGORY_FILTER_OPTIONS,
	EXPAND_FILTER_OPTIONS,
	SOURCE_FILTER_OPTIONS
} from './constants';

const renderCategoryValue = (selected) => {
	if (!selected?.length) {
		return 'All Categories';
	}

	return selected.map(getCategoryGroupLabel).join(', ');
};

const renderSourceValue = (selected) => {
	if (!selected?.length) {
		return 'All Sources';
	}

	return selected.map(getSourceFilterLabel).join(', ');
};

const renderExpandValue = (selected) => {
	if (!selected?.length) {
		return 'None';
	}

	return selected.map(getExpandGroupLabel).join(', ');
};

const renderCheckboxMenuItem = (option, selectedValues) => (
	<MenuItem key={option.value} value={option.value}>
		<Checkbox size="small" checked={selectedValues.includes(option.value)} />
		<ListItemText primary={option.label} />
	</MenuItem>
);

const AuditTrailFilterBar = ({ appliedFilters, onCommit }) => {
	const [draftFilters, setDraftFilters] = useState(() => cloneAppliedFilters(appliedFilters));
	const draftFiltersRef = useRef(draftFilters);

	useEffect(() => {
		const nextDraftFilters = cloneAppliedFilters(appliedFilters);
		setDraftFilters(nextDraftFilters);
		draftFiltersRef.current = nextDraftFilters;
	}, [appliedFilters]);

	const handleFilterChange = useCallback((field, value) => {
		setDraftFilters((previous) => {
			const nextDraftFilters = {
				...previous,
				[field]: value
			};
			draftFiltersRef.current = nextDraftFilters;
			return nextDraftFilters;
		});
	}, []);

	const handleFilterMenuClose = useCallback(() => {
		const latestDraftFilters = draftFiltersRef.current;

		if (areAuditTrailFiltersEqual(latestDraftFilters, appliedFilters)) {
			return;
		}

		onCommit(latestDraftFilters);
	}, [appliedFilters, onCommit]);

	const handleResetFilters = useCallback(() => {
		onCommit(buildClearedCategoryFilters(appliedFilters));
	}, [appliedFilters, onCommit]);

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr) auto' },
				alignItems: 'center',
				gap: 2,
				mb: 2
			}}
		>
			<FormControl fullWidth size="small">
				<InputLabel id="quote-audit-category-label" shrink>Category</InputLabel>
				<Select
					labelId="quote-audit-category-label"
					label="Category"
					multiple
					displayEmpty
					notched
					MenuProps={AUDIT_TRAIL_FILTER_MENU_PROPS}
					value={draftFilters.categoryGroups}
					renderValue={renderCategoryValue}
					onChange={(e) => handleFilterChange('categoryGroups', e.target.value)}
					onClose={handleFilterMenuClose}
				>
					{CATEGORY_FILTER_OPTIONS.map((option) =>
						renderCheckboxMenuItem(option, draftFilters.categoryGroups)
					)}
				</Select>
			</FormControl>
			<FormControl fullWidth size="small">
				<InputLabel id="quote-audit-source-label" shrink>Source</InputLabel>
				<Select
					labelId="quote-audit-source-label"
					label="Source"
					multiple
					displayEmpty
					notched
					MenuProps={AUDIT_TRAIL_FILTER_MENU_PROPS}
					value={draftFilters.sources}
					renderValue={renderSourceValue}
					onChange={(e) => handleFilterChange('sources', e.target.value)}
					onClose={handleFilterMenuClose}
				>
					{SOURCE_FILTER_OPTIONS.map((option) =>
						renderCheckboxMenuItem(option, draftFilters.sources)
					)}
				</Select>
			</FormControl>
			<FormControl fullWidth size="small">
				<InputLabel id="quote-audit-expand-label" shrink>Expand</InputLabel>
				<Select
					labelId="quote-audit-expand-label"
					label="Expand"
					multiple
					displayEmpty
					notched
					MenuProps={AUDIT_TRAIL_FILTER_MENU_PROPS}
					value={draftFilters.expandGroups}
					renderValue={renderExpandValue}
					onChange={(e) => handleFilterChange('expandGroups', e.target.value)}
					onClose={handleFilterMenuClose}
				>
					{EXPAND_FILTER_OPTIONS.map((option) =>
						renderCheckboxMenuItem(option, draftFilters.expandGroups)
					)}
				</Select>
			</FormControl>
			<Button
				variant="text"
				size="small"
				onClick={handleResetFilters}
				sx={{ justifySelf: { xs: 'end', md: 'start' }, whiteSpace: 'nowrap' }}
			>
				Reset filters
			</Button>
		</Box>
	);
};

export default memo(AuditTrailFilterBar);
